import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { ESSAY_TYPES } from '../data/prompts';
import { loadEssays, saveEssays } from '../data/storage';

// ── palette ──────────────────────────────────────────────────
const NAVY  = '#1a2a4a';
const GOLD  = '#b8860b';
const LGOLD = '#fef3c7';

const PARA_LABELS = ['Introduction', 'Body Paragraph 1', 'Body Paragraph 2', 'Conclusion'];

function wc(text) {
  return text?.trim() ? text.trim().split(/\s+/).filter(Boolean).length : 0;
}
function genCode() {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

// ── Smart Writing hints per paragraph ────────────────────────
const SMART_HINTS = {
  1: {
    title: 'Introduction — 3-part structure',
    steps: [
      { label: 'Paraphrase', hint: 'Rewrite the question completely in your own words. Do NOT copy any phrase directly from the question.' },
      { label: 'Context',    hint: 'Add 1–2 sentences of background explaining why this topic matters in the modern world.' },
      { label: 'Thesis',     hint: 'State your position clearly and signal the essay structure.' },
    ],
    phrases: [
      'It is widely argued that [topic] has become one of the most [pressing / significant] issues of the modern era.',
      'This essay will examine both perspectives before arguing that [your position].',
      'While some maintain that [view A], this essay will contend that [view B] ultimately provides the more compelling case.',
    ],
  },
  2: {
    title: 'Body Paragraph 1 — PEEL',
    steps: [
      { label: 'Point',    hint: 'State your main argument in one clear topic sentence.' },
      { label: 'Explain',  hint: 'Develop the point — explain WHY it is true and what the mechanism is.' },
      { label: 'Evidence', hint: 'Cite a specific country, statistic, or study. Always name it.' },
      { label: 'Link',     hint: 'Connect back to your argument using an academic conclusion phrase.' },
    ],
    phrases: [
      'Not only does [X], but it also [stronger second point].',
      'For example, [Country] [specific fact or statistic with year]. This demonstrates that [argument].',
      'It can therefore be argued that [conclusion linking back to topic sentence].',
    ],
  },
  3: {
    title: 'Body Paragraph 2 — Concession + Counter-argument',
    steps: [
      { label: 'Concession',   hint: 'Acknowledge the opposing view genuinely — do not dismiss it.' },
      { label: 'Counter',      hint: 'Introduce your stronger counter-argument using a contrast connector.' },
      { label: 'Evidence',     hint: 'Support with a specific, named example from a different country or context than Body 1.' },
      { label: 'Link',         hint: 'Use a sophisticated phrase to draw the analytical conclusion.' },
    ],
    phrases: [
      'Although [opposing view], [your stronger counter-argument].',
      'Despite [noun phrase concession], [main point].',
      'What this reveals is that [deeper analytical insight].',
      'The evidence therefore strongly suggests that [conclusion].',
    ],
  },
  4: {
    title: 'Conclusion — 3-part structure',
    steps: [
      { label: 'Restate',    hint: 'Restate your thesis in completely different words — never copy from your introduction.' },
      { label: 'Summarise',  hint: 'Briefly reference your two main supporting arguments in one sentence.' },
      { label: 'Final thought', hint: 'End with a recommendation, prediction, or broader implication.' },
    ],
    phrases: [
      'In conclusion, this essay has argued that [restate thesis in new words].',
      'It can therefore be concluded that [synthesis of both arguments].',
      'If [governments / individuals / society] were to [action], [predicted positive outcome].',
    ],
  },
};

// ── shared small components ───────────────────────────────────
function StatusBadge({ status }) {
  const cfg = {
    writing:   { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', label: '✏️ Writing' },
    submitted: { bg: '#f0faf4', color: '#15803d', border: '#bbf7d0', label: '✓ Submitted' },
  }[status] || { bg: '#f4f5f7', color: '#6b7280', border: '#e5e7eb', label: 'Joined' };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, borderRadius: 100, padding: '2px 11px', fontSize: 11.5, fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 600, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

// Elapsed timer (for teacher dashboard)
function ElapsedTimer({ startedAt }) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const origin = startedAt ? new Date(startedAt).getTime() : Date.now();
    const tick = () => setSecs(Math.floor((Date.now() - origin) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  const m = String(Math.floor(secs / 60)).padStart(2, '0');
  const s = String(secs % 60).padStart(2, '0');
  return <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: '.05em' }}>{m}:{s}</span>;
}

// Countdown timer (for student view when time_limit > 0)
function CountdownTimer({ startedAt, limitMinutes, onExpire }) {
  const totalSecs = limitMinutes * 60;
  const [remaining, setRemaining] = useState(totalSecs);

  useEffect(() => {
    const origin = startedAt ? new Date(startedAt).getTime() : Date.now();
    const tick = () => {
      const elapsed = Math.floor((Date.now() - origin) / 1000);
      const rem = Math.max(0, totalSecs - elapsed);
      setRemaining(rem);
      if (rem === 0 && onExpire) onExpire();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt, totalSecs, onExpire]);

  const m = String(Math.floor(remaining / 60)).padStart(2, '0');
  const s = String(remaining % 60).padStart(2, '0');
  const urgent = remaining < 120;
  const warning = remaining < 300;
  const color = urgent ? '#dc2626' : warning ? GOLD : '#15803d';

  return (
    <div style={{ background: urgent ? '#fff5f5' : warning ? LGOLD : '#f0faf4', border: `1px solid ${urgent ? '#fca5a5' : warning ? '#fde68a' : '#bbf7d0'}`, borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#9aa3b8', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 3 }}>Time Remaining</div>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 26, fontWeight: 700, color, letterSpacing: '.05em' }}>{m}:{s}</div>
      {urgent && <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, marginTop: 2 }}>Submit now!</div>}
    </div>
  );
}

const LS = { display: 'block', fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', color: '#5a6a88', marginBottom: 7 };
const SS = { width: '100%', padding: '10px 13px', border: '1.5px solid #dde3ed', borderRadius: 8, fontSize: 13.5, fontFamily: "'Plus Jakarta Sans',sans-serif", color: NAVY, background: '#fff', outline: 'none', boxSizing: 'border-box', marginBottom: 14 };

// ── Projection modal ──────────────────────────────────────────
function ProjectionModal({ response, onClose }) {
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '18px 40px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 700, color: NAVY }}>{response.student_name}</div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#9aa3b8', marginTop: 2 }}>{response.word_count || wc(response.paragraph_text)} words</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <StatusBadge status={response.status} />
          <button onClick={onClose} style={{ background: '#f0f4f8', border: 'none', borderRadius: 7, padding: '8px 18px', cursor: 'pointer', fontSize: 13, color: NAVY, fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 600 }}>
            ✕ Close (Esc)
          </button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '60px 100px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', fontFamily: "'Times New Roman',serif", fontSize: 24, lineHeight: 2.1, color: '#111', whiteSpace: 'pre-wrap' }}>
          {response.paragraph_text || <span style={{ color: '#ccc', fontStyle: 'italic' }}>No text yet…</span>}
        </div>
      </div>
    </div>
  );
}

// ── AI feedback mini-panel ────────────────────────────────────
function FeedbackPanel({ fb }) {
  if (!fb) return null;
  return (
    <div style={{ marginTop: 10, border: '1px solid #e8ecf2', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ background: NAVY, padding: '7px 13px', fontSize: 11.5, color: '#fff', fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', justifyContent: 'space-between' }}>
        <span>🤖 AI Feedback</span>
        {fb.band && <span style={{ color: GOLD }}>Band {fb.band}</span>}
      </div>
      <div style={{ padding: '10px 13px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {fb.strength    && <div style={{ background: '#f0faf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '7px 10px', fontSize: 12.5, color: '#14532d', lineHeight: 1.5 }}><strong>✓</strong> {fb.strength}</div>}
        {fb.improvement && <div style={{ background: '#fff8f0', border: '1px solid #fde8cc', borderRadius: 6, padding: '7px 10px', fontSize: 12.5, color: '#7c2d12', lineHeight: 1.5 }}><strong>→</strong> {fb.improvement}</div>}
        {fb.tip         && <div style={{ background: LGOLD,    border: '1px solid #fde68a', borderRadius: 6, padding: '7px 10px', fontSize: 12.5, color: '#92400e', lineHeight: 1.5 }}><strong>💡</strong> {fb.tip}</div>}
      </div>
    </div>
  );
}

// ── Smart Writing hints panel ─────────────────────────────────
function SmartHints({ paraNum }) {
  const [open, setOpen] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const hints = SMART_HINTS[paraNum] || SMART_HINTS[2];

  function copyPhrase(phrase, idx) {
    navigator.clipboard.writeText(phrase).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1800);
    });
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: open ? '#eff6ff' : '#f7f9fc', border: `1.5px solid ${open ? '#bfdbfe' : '#dde3ed'}`, borderRadius: 8, padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        <span style={{ fontWeight: 700, color: NAVY, fontSize: 13 }}>🧠 Smart Writing Hints — {hints.title}</span>
        <span style={{ color: '#9aa3b8', fontSize: 14 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ background: '#fff', border: '1.5px solid #bfdbfe', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Steps */}
          <div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6a88', marginBottom: 10 }}>Structure</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {hints.steps.map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ background: NAVY, color: '#fff', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1, fontFamily: "'JetBrains Mono',monospace" }}>{i + 1}</div>
                  <div>
                    <span style={{ fontWeight: 700, color: NAVY, fontSize: 12.5, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{step.label}: </span>
                    <span style={{ fontSize: 12.5, color: '#5a6a88', lineHeight: 1.5 }}>{step.hint}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Academic phrases */}
          <div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6a88', marginBottom: 8 }}>Useful Phrases — click to copy</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {hints.phrases.map((phrase, i) => (
                <button key={i} onClick={() => copyPhrase(phrase, i)}
                  style={{ background: copiedIdx === i ? '#f0faf4' : LGOLD, border: `1px solid ${copiedIdx === i ? '#bbf7d0' : '#fde68a'}`, borderRadius: 7, padding: '8px 12px', cursor: 'pointer', textAlign: 'left', fontSize: 12.5, fontFamily: "'Times New Roman',serif", color: copiedIdx === i ? '#14532d' : '#7c4f00', lineHeight: 1.55, transition: 'all .15s' }}>
                  {copiedIdx === i ? '✓ Copied!' : phrase}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TEACHER — Setup Screen
// ══════════════════════════════════════════════════════════════
function TeacherSetup({ prompts, onLaunch }) {
  const [essayType, setEssayType] = useState('discuss');
  const [promptId,  setPromptId]  = useState('');
  const [paraNum,   setParaNum]   = useState(1);
  const [mode,      setMode]      = useState('smart');
  const [limit,     setLimit]     = useState(15);
  const [launching, setLaunching] = useState(false);
  const [error,     setError]     = useState('');

  const list = prompts?.[essayType] || [];
  useEffect(() => { setPromptId(list[0]?.id?.toString() || ''); }, [essayType]);
  const selected = list.find(p => p.id?.toString() === promptId);

  async function launch() {
    if (!selected) { setError('Select a prompt first.'); return; }
    setLaunching(true); setError('');
    const code = genCode();
    const id   = `ses_${Date.now()}`;
    const { data, error: dbErr } = await supabase
      .from('live_sessions')
      .insert([{ id, code, prompt_text: selected.text, prompt_title: selected.title, essay_type: essayType, mode, paragraph_number: paraNum, time_limit: limit, status: 'active' }])
      .select().single();
    setLaunching(false);
    if (dbErr) { setError('Could not start session: ' + dbErr.message); return; }
    onLaunch(data);
  }

  const chip = (active, onClick, children) => (
    <button onClick={onClick} style={{ border: `1.5px solid ${active ? GOLD : '#dde3ed'}`, background: active ? LGOLD : '#fff', color: active ? '#92400e' : '#5a6a88', borderRadius: 7, padding: '7px 16px', fontSize: 12.5, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: active ? 600 : 400, transition: 'all .12s' }}>
      {children}
    </button>
  );

  return (
    <div>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: NAVY, marginBottom: 20 }}>🚀 Start a Live Session</div>

      <label style={LS}>Essay Type</label>
      <select value={essayType} onChange={e => setEssayType(e.target.value)} style={SS}>
        {ESSAY_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>

      <label style={LS}>Select Prompt</label>
      <select value={promptId} onChange={e => setPromptId(e.target.value)} style={SS}>
        {list.map(p => <option key={p.id} value={p.id?.toString()}>{p.title}</option>)}
      </select>

      {selected && (
        <div style={{ background: '#f7f9fc', border: '1px solid #dde3ed', borderRadius: 8, padding: '11px 14px', fontSize: 13.5, color: '#374151', lineHeight: 1.65, marginBottom: 16, fontFamily: "'Times New Roman',serif", fontStyle: 'italic' }}>
          {selected.text}
        </div>
      )}

      <label style={LS}>Paragraph</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {PARA_LABELS.map((l, i) => chip(paraNum === i + 1, () => setParaNum(i + 1), l))}
      </div>

      <label style={LS}>Writing Mode</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['smart', '🧠 Smart Writing', '(formula hints shown)'], ['free', '✏️ Free Writing', '(no hints)']].map(([v, l, sub]) => (
          <button key={v} onClick={() => setMode(v)}
            style={{ border: `1.5px solid ${mode === v ? NAVY : '#dde3ed'}`, background: mode === v ? NAVY : '#fff', color: mode === v ? '#fff' : '#5a6a88', borderRadius: 7, padding: '9px 18px', fontSize: 13, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 600 }}>
            {l} <span style={{ fontSize: 11, fontWeight: 400, opacity: .8 }}>{sub}</span>
          </button>
        ))}
      </div>

      <label style={LS}>Time Limit</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {[[5,'5 min'],[10,'10 min'],[15,'15 min'],[20,'20 min'],[0,'No limit']].map(([v,l]) =>
          chip(limit === v, () => setLimit(v), l)
        )}
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 12.5, marginBottom: 10 }}>{error}</div>}

      <button onClick={launch} disabled={launching || !selected}
        style={{ background: launching ? '#9aa3b8' : NAVY, color: '#fff', border: 'none', padding: '13px 36px', borderRadius: 9, fontSize: 14.5, fontWeight: 700, cursor: launching ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", boxShadow: '0 3px 10px rgba(26,42,74,.2)' }}>
        {launching ? '⏳ Launching…' : '🚀 Launch Session'}
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TEACHER — Active Session Dashboard
// ══════════════════════════════════════════════════════════════
function TeacherSession({ session }) {
  const [responses,  setResponses]  = useState([]);
  const [viewing,    setViewing]    = useState(null);
  const [projecting, setProjecting] = useState(null);
  const [ended,      setEnded]      = useState(session.status === 'ended');
  const [aiLoading,  setAiLoading]  = useState(false);
  const [aiDone,     setAiDone]     = useState(false);
  const [copied,     setCopied]     = useState(false);
  const [toSave,     setToSave]     = useState(new Set());
  const [savedCount, setSavedCount] = useState(0);

  const joinUrl = `${window.location.origin}${window.location.pathname}?session=${session.code}`;
  const qrUrl   = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(joinUrl)}`;

  // Initial load + real-time subscription
  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('session_responses').select('*').eq('session_id', session.id).order('created_at');
      if (data) setResponses(data);
    }
    load();
    const ch = supabase.channel(`live_${session.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'session_responses', filter: `session_id=eq.${session.id}` },
        p => setResponses(prev => prev.find(r => r.id === p.new.id) ? prev : [...prev, p.new]))
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'session_responses', filter: `session_id=eq.${session.id}` },
        p => {
          setResponses(prev => prev.map(r => r.id === p.new.id ? { ...r, ...p.new } : r));
          setViewing(v => v?.id === p.new.id ? { ...v, ...p.new } : v);
          setProjecting(v => v?.id === p.new.id ? { ...v, ...p.new } : v);
        })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [session.id]);

  async function endSession() {
    await supabase.from('live_sessions').update({ status: 'ended', ended_at: new Date().toISOString() }).eq('id', session.id);
    setEnded(true);
  }

  async function requestFeedback() {
    const todo = responses.filter(r => r.status === 'submitted' && !r.ai_feedback);
    if (!todo.length) return;
    setAiLoading(true);
    for (const resp of todo) {
      try {
        const res = await fetch('/api/anthropic', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: 'claude-sonnet-4-5',
            max_tokens: 350,
            system: 'You are an IELTS examiner. Give brief, specific feedback on a student paragraph. Return ONLY valid JSON.',
            messages: [{ role: 'user', content: `Prompt: "${session.prompt_text}"\n\nStudent paragraph (${PARA_LABELS[(session.paragraph_number || 1) - 1]}):\n"${resp.paragraph_text}"\n\nReturn ONLY JSON: {"band":"6.5","strength":"one specific strength, quoting their exact words","improvement":"one specific structural or vocabulary issue to fix","tip":"one clear actionable tip for their next paragraph"}` }],
          }),
        });
        const d  = await res.json();
        const fb = JSON.parse((d.content?.[0]?.text || '{}').replace(/```json|```/g, '').trim());
        await supabase.from('session_responses').update({ ai_feedback: fb }).eq('id', resp.id);
        setResponses(prev => prev.map(r => r.id === resp.id ? { ...r, ai_feedback: fb } : r));
        setViewing(v => v?.id === resp.id ? { ...v, ai_feedback: fb } : v);
      } catch (e) { console.error('AI feedback error:', e); }
    }
    setAiLoading(false); setAiDone(true);
  }

  function saveSelected() {
    const existing = loadEssays();
    const paraLabel = PARA_LABELS[(session.paragraph_number || 1) - 1];
    const newEssays = responses
      .filter(r => r.status === 'submitted' && toSave.has(r.id))
      .map(r => ({
        id:          `live_${r.id}`,
        title:       `${session.prompt_title} — ${paraLabel}`,
        text:        r.paragraph_text,
        student:     r.student_name,
        date:        new Date().toLocaleDateString('en-GB'),
        type:        session.essay_type,
        paragraph:   paraLabel,
        sessionCode: session.code,
        wordCount:   r.word_count || wc(r.paragraph_text),
        feedback:    r.ai_feedback || null,
        source:      'live-session',
      }));
    saveEssays([...existing, ...newEssays]);
    setSavedCount(newEssays.length);
    setToSave(new Set());
  }

  function toggleSave(id) {
    setToSave(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function copyLink() {
    navigator.clipboard.writeText(joinUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  const joined    = responses.length;
  const submitted = responses.filter(r => r.status === 'submitted').length;

  return (
    <div>
      {projecting && <ProjectionModal response={projecting} onClose={() => setProjecting(null)} />}

      {/* ── Session banner ── */}
      <div style={{ background: NAVY, borderRadius: 12, padding: '22px 24px', marginBottom: 18, display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'stretch' }}>
        {/* Code + link */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, color: '#9aa3b8', letterSpacing: '.14em', textTransform: 'uppercase', marginBottom: 6 }}>Live Session Code</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 54, color: '#fff', fontWeight: 700, letterSpacing: '.12em', lineHeight: 1 }}>{session.code}</div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={copyLink} style={{ background: copied ? '#15803d' : GOLD, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700 }}>
              {copied ? '✓ Copied!' : '🔗 Copy Join Link'}
            </button>
          </div>
          <div style={{ marginTop: 7, fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#6b7ea8', wordBreak: 'break-all' }}>{joinUrl}</div>
        </div>

        {/* QR code */}
        <div style={{ background: '#fff', padding: 8, borderRadius: 9, alignSelf: 'flex-start', flexShrink: 0 }}>
          <img src={qrUrl} alt="QR" width={130} height={130} style={{ display: 'block' }} />
        </div>

        {/* Stats + timer + end button */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center', flexShrink: 0, minWidth: 150 }}>
          {!ended && (
            <div style={{ background: 'rgba(255,255,255,.07)', borderRadius: 8, padding: '10px 16px', textAlign: 'center' }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#9aa3b8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.1em' }}>
                {session.time_limit > 0 ? `Limit: ${session.time_limit} min` : 'Elapsed'}
              </div>
              <ElapsedTimer startedAt={session.created_at} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ background: 'rgba(255,255,255,.07)', borderRadius: 8, padding: '10px 14px', textAlign: 'center', flex: 1 }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 22, color: '#fff', fontWeight: 700 }}>{joined}</div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#9aa3b8', textTransform: 'uppercase', letterSpacing: '.08em' }}>Joined</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,.07)', borderRadius: 8, padding: '10px 14px', textAlign: 'center', flex: 1 }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 22, color: GOLD, fontWeight: 700 }}>{submitted}</div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#9aa3b8', textTransform: 'uppercase', letterSpacing: '.08em' }}>Submitted</div>
            </div>
          </div>
          {!ended && (
            <button onClick={endSession} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, padding: '9px', fontSize: 13, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700 }}>
              ■ End Session
            </button>
          )}
          {ended && (
            <div style={{ background: '#f0faf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: '8px 12px', textAlign: 'center', fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 12, color: '#15803d', fontWeight: 700 }}>
              ✓ Session Ended
            </div>
          )}
        </div>
      </div>

      {/* ── Post-session action bar ── */}
      {ended && (
        <div style={{ background: '#f7f9fc', border: '1px solid #dde3ed', borderRadius: 10, padding: '14px 18px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, fontSize: 13.5, color: '#374151', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Session ended — <strong>{submitted}</strong> of <strong>{joined}</strong> students submitted.
          </div>
          {submitted > 0 && API_KEY && (
            <button onClick={requestFeedback} disabled={aiLoading || aiDone}
              style={{ background: aiDone ? '#15803d' : NAVY, color: '#fff', border: 'none', borderRadius: 7, padding: '9px 16px', fontSize: 12.5, cursor: aiLoading || aiDone ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700 }}>
              {aiDone ? '✓ Feedback ready' : aiLoading ? '⏳ Analysing…' : '🤖 Request AI Feedback for All'}
            </button>
          )}
          {submitted > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {toSave.size > 0 && (
                <button onClick={saveSelected}
                  style={{ background: GOLD, color: '#fff', border: 'none', borderRadius: 7, padding: '9px 16px', fontSize: 12.5, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700 }}>
                  💾 Save Selected ({toSave.size}) to Essay Library
                </button>
              )}
              {savedCount > 0 && (
                <span style={{ fontSize: 12.5, color: '#15803d', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 600 }}>✓ {savedCount} saved</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Viewing panel ── */}
      {viewing && (
        <div style={{ background: '#fff', border: '2px solid #dde3ed', borderRadius: 10, padding: '16px 18px', marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: 15, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{viewing.student_name}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <StatusBadge status={viewing.status} />
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#9aa3b8' }}>{viewing.word_count || wc(viewing.paragraph_text)} words</span>
              <button onClick={() => setProjecting(viewing)} style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 13px', fontSize: 12, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 600 }}>📽 Project</button>
              <button onClick={() => setViewing(null)} style={{ background: '#f4f5f7', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 13, cursor: 'pointer', color: '#5a6a88' }}>✕</button>
            </div>
          </div>
          <div style={{ background: '#fafafa', border: '1px solid #eeeeee', borderRadius: 8, padding: '14px 16px', minHeight: 90, fontFamily: "'Times New Roman',serif", fontSize: 14.5, lineHeight: 1.9, color: '#1a1a1a', whiteSpace: 'pre-wrap' }}>
            {viewing.paragraph_text || <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>Student is typing…</span>}
          </div>
          <FeedbackPanel fb={viewing.ai_feedback} />
        </div>
      )}

      {/* ── Student cards ── */}
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', color: '#5a6a88', marginBottom: 10 }}>
        {joined === 0 ? 'Waiting for students…' : `Students (${joined})`}
        {ended && submitted > 0 && (
          <span style={{ marginLeft: 12, fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 11, color: GOLD, fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
            ← check boxes to save to Essay Library
          </span>
        )}
      </div>

      {joined === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 20px', border: '2px dashed #dde3ed', borderRadius: 12, color: '#9aa3b8' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📡</div>
          <div style={{ fontSize: 13.5 }}>Share <strong style={{ color: NAVY, fontFamily: "'JetBrains Mono',monospace", fontSize: 20 }}>{session.code}</strong> with your students.</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>They appear here automatically when they join.</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {responses.map(r => {
          const live     = viewing?.id === r.id ? viewing : r;
          const isViewed = viewing?.id === r.id;
          const checked  = toSave.has(r.id);
          return (
            <div key={r.id} style={{ background: '#fff', border: `1.5px solid ${live.status === 'submitted' ? '#bbf7d0' : '#e8ecf2'}`, borderRadius: 9, padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 12, transition: 'border-color .2s' }}>
              {/* Save checkbox (only shown after session ends for submitted) */}
              {ended && live.status === 'submitted' && (
                <input type="checkbox" checked={checked} onChange={() => toggleSave(r.id)}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: GOLD, flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: NAVY, fontSize: 13.5, fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: 5 }}>{live.student_name}</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <StatusBadge status={live.status} />
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10.5, color: '#9aa3b8' }}>{live.word_count || 0} words</span>
                  {live.ai_feedback && <span style={{ background: LGOLD, color: '#92400e', border: '1px solid #fde68a', borderRadius: 100, padding: '1px 9px', fontSize: 10.5, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>AI ✓</span>}
                </div>
                {/* Inline AI feedback summary when viewing is closed */}
                {!isViewed && live.ai_feedback && (
                  <div style={{ marginTop: 5, fontSize: 12, color: '#15803d', fontFamily: "'Plus Jakarta Sans',sans-serif", lineHeight: 1.4 }}>
                    <strong>Band {live.ai_feedback.band}</strong> · {live.ai_feedback.strength?.slice(0, 70)}…
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => setViewing(isViewed ? null : live)}
                  style={{ background: isViewed ? '#e8f0fb' : '#f7f9fc', border: `1px solid ${isViewed ? '#c0d4f0' : '#dde3ed'}`, color: NAVY, borderRadius: 7, padding: '6px 13px', fontSize: 12, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 600 }}>
                  {isViewed ? '✕ Close' : '👁 View'}
                </button>
                <button onClick={() => setProjecting(live)}
                  style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 7, padding: '6px 11px', fontSize: 14, cursor: 'pointer' }} title="Project full-screen">
                  📽
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// STUDENT — Join Screen
// ══════════════════════════════════════════════════════════════
function StudentJoin({ initialCode, onJoined }) {
  const [name,    setName]    = useState('');
  const [code,    setCode]    = useState(initialCode || '');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  async function join() {
    if (!name.trim())      { setError('Please enter your name.'); return; }
    if (code.length !== 6) { setError('Please enter the 6-digit session code.'); return; }
    setLoading(true); setError('');

    const { data: sess, error: sErr } = await supabase
      .from('live_sessions').select('*').eq('code', code).eq('status', 'active').maybeSingle();

    if (sErr || !sess) {
      setError('Session not found or has ended. Check the code and try again.');
      setLoading(false); return;
    }

    const { data: resp, error: rErr } = await supabase
      .from('session_responses')
      .insert([{ session_id: sess.id, student_name: name.trim(), paragraph_text: '', word_count: 0, status: 'writing' }])
      .select().single();

    setLoading(false);
    if (rErr) { setError('Could not join. Please try again.'); return; }
    onJoined(sess, resp);
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#0d1b35 0%,#1a2a4a 60%,#22334f 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '30px 20px' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '40px 36px', maxWidth: 420, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,.28)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 30, fontWeight: 700, color: NAVY }}>IELTS<span style={{ color: GOLD }}>Compass</span></div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, color: '#9aa3b8', letterSpacing: '.14em', textTransform: 'uppercase', marginTop: 4 }}>📡 Live Session</div>
        </div>

        <label style={LS}>Your Name</label>
        <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && join()}
          placeholder="e.g. Ahmed Hassan"
          style={{ ...SS, fontSize: 14 }}
        />

        <label style={LS}>Session Code</label>
        <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={e => e.key === 'Enter' && join()}
          placeholder="000000"
          style={{ ...SS, fontSize: 28, fontFamily: "'JetBrains Mono',monospace", textAlign: 'center', letterSpacing: '.28em' }}
        />

        {error && <div style={{ color: '#dc2626', fontSize: 12.5, marginBottom: 10, lineHeight: 1.4 }}>{error}</div>}

        <button onClick={join} disabled={loading}
          style={{ width: '100%', background: loading ? '#9aa3b8' : NAVY, color: '#fff', border: 'none', padding: '13px', borderRadius: 9, fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", boxShadow: '0 3px 10px rgba(26,42,74,.25)', marginTop: 4 }}>
          {loading ? '⏳ Joining…' : '→ Join Session'}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// STUDENT — Writing Screen
// ══════════════════════════════════════════════════════════════
function StudentWriting({ session, response, onSubmit }) {
  const [text,       setText]      = useState('');
  const [saving,     setSaving]    = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sessEnded,  setSessEnded] = useState(false);
  const [timeUp,     setTimeUp]    = useState(false);
  const saveRef = useRef(null);

  const words     = wc(text);
  const paraLabel = PARA_LABELS[(session.paragraph_number || 1) - 1] || 'Paragraph';
  const hasLimit  = session.time_limit > 0;

  // Auto-save debounce — 3 s after typing stops
  function handleChange(val) {
    setText(val);
    if (saveRef.current) clearTimeout(saveRef.current);
    saveRef.current = setTimeout(async () => {
      setSaving(true);
      const w = wc(val);
      await supabase.from('session_responses').update({ paragraph_text: val, word_count: w }).eq('id', response.id);
      setSaving(false);
    }, 3000);
  }
  useEffect(() => () => { if (saveRef.current) clearTimeout(saveRef.current); }, []);

  // Listen for session end
  useEffect(() => {
    const ch = supabase.channel(`sess_status_${session.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'live_sessions', filter: `id=eq.${session.id}` },
        p => { if (p.new?.status === 'ended') setSessEnded(true); })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [session.id]);

  // Time expired callback
  const handleTimeUp = () => { setTimeUp(true); };

  async function submit() {
    if (words < 5) return;
    setSubmitting(true);
    if (saveRef.current) clearTimeout(saveRef.current);
    await supabase.from('session_responses').update({ paragraph_text: text, word_count: words, status: 'submitted', submitted_at: new Date().toISOString() }).eq('id', response.id);
    setSubmitting(false);
    onSubmit();
  }

  const wordColor = words < 50 ? '#dc2626' : words < 100 ? GOLD : '#15803d';

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: NAVY, padding: '13px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 700, color: '#fff' }}>IELTS<span style={{ color: GOLD }}>Compass</span></div>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#9aa3b8' }}>{response.student_name} · {session.code}</div>
      </div>

      {/* Alerts */}
      {(sessEnded || timeUp) && (
        <div style={{ background: timeUp ? '#fff5f5' : '#fef3c7', borderBottom: `1px solid ${timeUp ? '#fca5a5' : '#fde68a'}`, padding: '10px 20px', fontSize: 13.5, color: timeUp ? '#dc2626' : '#92400e', textAlign: 'center', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700 }}>
          {timeUp ? '⏱ Time is up! Please submit your paragraph now.' : '⚠️ The teacher has ended the session — submit your paragraph now.'}
        </div>
      )}

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 18px', flex: 1, width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 0 }}>

        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 16 }}>
          {/* Prompt + task */}
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ background: NAVY, borderRadius: 10, padding: '15px 18px', marginBottom: 14 }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8.5, color: '#9aa3b8', letterSpacing: '.14em', textTransform: 'uppercase', marginBottom: 6 }}>
                {ESSAY_TYPES.find(t => t.id === session.essay_type)?.label || session.essay_type}
              </div>
              <div style={{ fontSize: 14, color: '#fff', lineHeight: 1.7, fontFamily: "'Times New Roman',serif" }}>{session.prompt_text}</div>
            </div>

            {/* Task instruction */}
            <div style={{ background: LGOLD, border: `1px solid #fde68a`, borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>✏️</span>
              <div>
                <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, color: '#92400e', fontSize: 13.5 }}>Write your {paraLabel}</div>
                <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 1 }}>
                  {session.mode === 'smart' ? 'Smart Writing mode — tap "Hints" below for structure guidance' : 'Free Writing mode — write naturally'}
                </div>
              </div>
            </div>
          </div>

          {/* Countdown (only if time_limit set) */}
          {hasLimit && (
            <div style={{ flexShrink: 0 }}>
              <CountdownTimer startedAt={session.created_at} limitMinutes={session.time_limit} onExpire={handleTimeUp} />
            </div>
          )}
        </div>

        {/* Smart Writing hints panel */}
        {session.mode === 'smart' && (
          <SmartHints paraNum={session.paragraph_number || 1} />
        )}

        {/* Textarea */}
        <textarea
          value={text}
          onChange={e => handleChange(e.target.value)}
          placeholder={`Start your ${paraLabel} here…`}
          rows={12}
          style={{ width: '100%', background: '#fff', border: '1.5px solid #dde3ed', borderRadius: 10, padding: '16px 18px', fontSize: 15.5, lineHeight: 1.95, fontFamily: "'Times New Roman',serif", color: '#1a1a1a', outline: 'none', resize: 'vertical', boxSizing: 'border-box', transition: 'border-color .2s' }}
        />

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 14, color: wordColor, fontWeight: 700 }}>{words} words</span>
            {saving && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#9aa3b8' }}>saving…</span>}
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#c8d0e0' }}>Target: 100–150 words</span>
          </div>
          <button onClick={submit} disabled={submitting || words < 5}
            style={{ background: words < 5 ? '#d1d5db' : GOLD, color: '#fff', border: 'none', borderRadius: 8, padding: '12px 30px', fontSize: 14, fontWeight: 700, cursor: words < 5 ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", boxShadow: words >= 5 ? '0 3px 10px rgba(184,134,11,.35)' : 'none', transition: 'all .15s' }}>
            {submitting ? '⏳ Submitting…' : 'Submit Paragraph ✓'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// STUDENT — Submitted Screen
// ══════════════════════════════════════════════════════════════
function StudentSubmitted({ name }) {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#0d1b35 0%,#1a2a4a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '52px 40px', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 12px 40px rgba(0,0,0,.25)' }}>
        <div style={{ fontSize: 56, marginBottom: 18 }}>✅</div>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 700, color: NAVY, marginBottom: 10 }}>Paragraph Submitted!</div>
        <div style={{ fontSize: 14, color: '#5a6a88', lineHeight: 1.7, marginBottom: 28 }}>
          Well done, <strong>{name}</strong>.<br />
          Your paragraph has been sent to your teacher.
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 14 }}>
          {[0, 1, 2].map(i => (
            <span key={i} style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: GOLD, animation: `blink 1.4s ease-in-out ${i * 0.22}s infinite` }} />
          ))}
        </div>
        <style>{`@keyframes blink{0%,100%{opacity:.3;transform:scale(1)}50%{opacity:1;transform:scale(1.45)}}`}</style>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10.5, color: '#9aa3b8', letterSpacing: '.06em' }}>Waiting for teacher…</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════
export default function LiveSession({ isTeacher, prompts, initialCode }) {
  // ── Teacher state ──
  const [tView,   setTView]   = useState('setup');
  const [session, setSession] = useState(null);

  // ── Student state ──
  const [sView,    setSView]    = useState('join');
  const [sSession, setSSession] = useState(null);
  const [sResp,    setSResp]    = useState(null);

  if (isTeacher) {
    return (
      <div>
        {tView === 'setup' ? (
          <TeacherSetup prompts={prompts} onLaunch={s => { setSession(s); setTView('session'); }} />
        ) : (
          <>
            <TeacherSession session={session} />
            <div style={{ marginTop: 22 }}>
              <button onClick={() => { setSession(null); setTView('setup'); }}
                style={{ background: 'none', border: '1px solid #dde3ed', color: '#5a6a88', borderRadius: 7, padding: '7px 18px', fontSize: 12.5, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                ← Start New Session
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  if (sView === 'join')      return <StudentJoin initialCode={initialCode} onJoined={(sess, resp) => { setSSession(sess); setSResp(resp); setSView('writing'); }} />;
  if (sView === 'writing')   return <StudentWriting session={sSession} response={sResp} onSubmit={() => setSView('submitted')} />;
  return <StudentSubmitted name={sResp?.student_name || ''} />;
}
